import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

export default function CancellationPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="cancellation-page">
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-2">Cancellation & Refund Policy</h1>
        <p className="text-lg text-muted-foreground mb-2">Please review our cancellation and refund terms carefully</p>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: Jan 01, 2025 | Last Updated: Aug 23, 2025</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Scope</h2>
            <p className="text-muted-foreground leading-relaxed">
              This policy applies to all transactions made on <a href="https://www.prowentech.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.prowentech.com</a> and processed via Razorpay.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Cancellations</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may request to cancel your order or subscription within 24 hours of purchase if service or delivery has not commenced. To initiate a cancellation, please contact us at <a href="mailto:support@prowentech.com" className="text-primary hover:underline">support@prowentech.com</a> with your order details.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Refund Conditions</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">Refunds may be issued in the following scenarios:</p>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li>Duplicate payments</li>
              <li>Failed or incomplete services</li>
              <li>Cancellations within eligible timeframes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Refunds will be processed to the original payment method used via Razorpay.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Refund Timelines</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Refunds will be processed within 5 to 7 business days. Depending on your payment method, it may take additional time for the amount to reflect in your account:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li><strong className="text-foreground">Credit/Debit Cards:</strong> 5-10 business days</li>
              <li><strong className="text-foreground">UPI/Net Banking:</strong> 2-7 business days</li>
              <li><strong className="text-foreground">Wallets:</strong> Instant or within 24 hours</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Contact Information</h2>
            <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground space-y-1">
              <p>Email: <a href="mailto:support@prowentech.com" className="text-primary hover:underline">support@prowentech.com</a></p>
              <p>Phone: +91-9087804440</p>
            </div>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Refund Communication</h2>
            <p className="text-muted-foreground leading-relaxed">
              Once initiated, Razorpay will email you a Refund ID and UTR/Reference Number to track the status.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Non-Refundable Cases</h2>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li>Services already rendered</li>
              <li>Payments made more than 7 days prior</li>
              <li>Payment gateway charges (if any) may not be refunded</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Disputes and Chargebacks</h2>
            <p className="text-muted-foreground leading-relaxed">
              In the event of disputes or chargebacks, please reach out to our support team first. We will cooperate fully to resolve issues. If unresolved, you may initiate a dispute through your payment provider.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
